/**
 * @file spacing.ts
 *
 * @fileoverview Spacing utility methods for generating classes and types for components that need to use
 * dynamic responsive spacing like margin or padding.
 */

import { jobiqoCSSModuleClasses } from "../types/cssModules";
import { getClasses } from "../utils/responsive";

export const marginProperties = [
  "margin",
  "marginTop",
  "marginBottom",
  "marginLeft",
  "marginRight",
] as const;
type MarginProps = {
  margin?: number | string | number[] | string[];
  marginTop?: number | string | number[] | string[];
  marginBottom?: number | string | number[] | string[];
  marginLeft?: number | string | number[] | string[];
  marginRight?: number | string | number[] | string[];
};

export const generateMarginClasses = (
  { margin, marginTop, marginBottom, marginLeft, marginRight }: MarginProps,
  styles: jobiqoCSSModuleClasses | null = null
): string => {
  return `
    ${getClasses(styles, "m", margin)}
    ${getClasses(styles, "mt", marginTop)}
    ${getClasses(styles, "mb", marginBottom)}
    ${getClasses(styles, "ml", marginLeft)}
    ${getClasses(styles, "mr", marginRight)}
  `.trim();
};

export const paddingProperties = [
  "padding",
  "paddingTop",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
];
type PaddingProps = {
  padding?: number | string | number[] | string[];
  paddingTop?: number | string | number[] | string[];
  paddingBottom?: number | string | number[] | string[];
  paddingLeft?: number | string | number[] | string[];
  paddingRight?: number | string | number[] | string[];
};
export const generatePaddingClasses = (
  {
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
  }: PaddingProps,
  styles: jobiqoCSSModuleClasses | null = null
): string => {
  return `
    ${getClasses(styles, "p", padding)}
    ${getClasses(styles, "pt", paddingTop)}
    ${getClasses(styles, "pb", paddingBottom)}
    ${getClasses(styles, "pl", paddingLeft)}
    ${getClasses(styles, "pr", paddingRight)}
  `.trim();
};

/**
 * Excludes any props related to spacing from the list of props.
 *
 * To avoid React warnings of elements passed to the DOM such as "marginLeft" we have to
 * exclude unwanted props from being rendered to the DOM.
 *
 * @param props
 *   The list of props from the component.
 * @returns
 *   The list of props without the props related to spacing.
 */
export function excludeSpacingProps(props) {
  let propsWithoutSpacingElements = {};
  Object.keys(props).forEach((prop: any) => {
    if (!marginProperties.includes(prop) && !paddingProperties.includes(prop)) {
      propsWithoutSpacingElements = {
        ...propsWithoutSpacingElements,
        ...{
          [prop]: props[prop],
        },
      };
    }
  });
  return propsWithoutSpacingElements;
}

export type SpaceProps = MarginProps & PaddingProps;
